import { Injectable } from '@angular/core';
import {
  AutoCompleteService,
  GrupoResposta,
  HttpClient,
  NumberUtils,
  Page,
  PageParameters
} from '@elotech/angular-common';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  FuncionarioDTO
} from '../acao-coletiva/acao-coletiva-mudanca-lotacao/table/acao-coletiva-mudanca-lotacao-funcionario.service';
import { PortalServidor } from '../common/annotations/portal-servidor';
import { FuncionarioNotificationDto } from '../common/components/funcionario-delete/funcionario-notification-dto';
import { SelecaoMatrizSalarial } from '../common/components/selecao-matriz-salarial/selecao-matriz-salarial.model';
import { EnumItem } from '../common/enum-item';
import { EsocialService } from '../common/services/esocial-service';
import { ConvenioBancario } from '../convenio-bancario/convenio-bancario';
import { Funcionario } from '../lancamento/model';
import { FuncionarioSimplificadoDTO } from './FuncionarioSimplificadoDTO';
import { Produtividade } from '../produtividade/produtividade';
import { FuncionarioReduzido } from '../common/interface/funcionario-reduzido';
import { FuncionarioOption } from "../common/interface/funcionario-option";
import { FuncionarioQuadroSalarial } from '../common/interface/funcionario-quadro-salarial';
import { TableServidorReduzido } from "../common/interface/table-servidor-reduzido";

@Injectable()
export class FuncionarioService extends EsocialService<any> implements AutoCompleteService {
  constructor(public httpClient: HttpClient) {
    super(httpClient);
    this.resource = '/api/funcionarios';
  }

  public loadFieldModels(fields: string = ''): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/custom/model`,
      fields
    });
  }

  findToOptions(searchValue: string): Observable<any[]> {
    return this.findAllReduzido(
      new PageParameters(15, 0, 'matricula,asc'),
      this.getSearch(searchValue),
      this.fields.toString(),
      true
    ).pipe(map((page) => page.content));
  }

  getSearch(searchValue) {
    return `(pessoa.nome == "*${searchValue}*" ${
      NumberUtils.isNumeric(searchValue) ? ', matricula == ' + searchValue : ''
    })`;
  }

  objToOption(funcionario: any): string {
    if (!funcionario?.matricula) {
      return '';
    }

    const nome = funcionario.pessoa?.nome || funcionario.nome || '';
     return nome ? `${funcionario.matricula} - ${nome}` : `${funcionario.matricula}`;
  }

  findToOptionFuncionarioEPrevidenciaNotDesligado(searchValue: string): Observable<any[]> {
    return this.findAll(
      new PageParameters(15, 0, 'matricula,asc'),
      this.getSearch(searchValue) + ';situacaoFuncional.situacao != DESLIGADO',
      'id,dataAdmissao,dataDesligamento,tipoCadastro,pessoa.id,pessoa.nome,matricula,classe.id,classe.codigo,classe.descricao,classe.previdencia',
      true
    ).pipe(map((page) => page.content));
  }

  findToOptionsNotDesligado(searchValue: string) {
    return this.findAllReduzido(
      new PageParameters(20, 0, 'matricula,asc'),
      this.getSearch(searchValue) + ';situacaoFuncional.situacao != DESLIGADO'
    ).pipe(map((page) => page.content));
  }

  getVidaFuncional(
    id: number,
    paginacao: any,
    search: string,
    disableSpinner: boolean
  ): Observable<Page<any>> {
    const requestParams = new PageParameters(
      paginacao.maxResult,
      paginacao.page,
      'data,desc'
    );

    return this.httpClient.get({
      disableSpinner: disableSpinner,
      search,
      page: requestParams,
      resource: `${this.resource}/vida-funcional/${id}`,
    });
  }

  public getMatricula(): Observable<number> {
    return this.httpClient.get({
      disableSpinner: true,
      resource: `${this.resource}/matricula`,
    });
  }

  public isMatriculaJaCadastrada(id: number, matricula: number): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/matricula-ja-cadastrada/${id ?? 0}/${matricula}`,
      disableSpinner: true,
    });
  }

  public findFuncionariosBarraLateral(search: string): Observable<any> {
    search = encodeURI(search);
    return this.httpClient.get({
      resource: `${this.resource}/filter-barra-lateral?search=${search}`,
      disableSpinner: true,
    });
  }

  @PortalServidor()
  public findFuncionariosPorCpf(cpf: string): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/funcionarios-por-cpf/${cpf}`,
      disableSpinner: true,
    });
  }

  public getTotalFuncionarios(
    situacao: any,
    naCompetencia = false
  ): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/total-por-situacao?naCompetencia=${naCompetencia}`,
      search: situacao,
      disableSpinner: true,
    });
  }

  public isClasseUtilizada(idClasse: number): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/classe-utilizada`,
      id: idClasse,
      disableSpinner: true,
    });
  }

  public contarFuncionariosNaEstrutura(idEstrutura: number, data?: Date) {
    const dataParam = data ? `?data=${data}` : '';
    return this.httpClient.get({
      resource: `${this.resource}/estrutura-organizacional/${idEstrutura}${dataParam}`,
    });
  }

  existeFuncionarioForaDoIntervalo(
    planoCargoId: number,
    selecao: SelecaoMatrizSalarial
  ): Observable<ForaDoIntervaloDTO> {
    return this.httpClient.post({
      resource: `${this.resource}/existe-fora-do-intervalo`,
      body: {
        planoCargoId,
        selecaoInicial: selecao.inicial,
        selecaoFinal: selecao.final,
      },
    });
  }

  loadCargoNomeado(funcionarioId: number): Observable<any> {
    if (!funcionarioId) {
      return of();
    }
    return this.httpClient.get({
      resource: `${this.resource}/cargo-nomeado/${funcionarioId}`,
    });
  }

  post(entity: any, fields?: string): Observable<any> {
    return this.httpClient.post({
      resource: `${this.resource}/dto`,
      body: entity,
      fields: this.calculeFields(fields),
      location: this.location,
    });
  }

  put(id: number, entity: any, fields?: string): Observable<any> {
    return this.httpClient.put({
      resource: `${this.resource}/dto`,
      body: entity,
      id,
      fields: this.calculeFields(fields),
      location: this.location,
    });
  }

  remove(id: number): Observable<FuncionarioNotificationDto> {
    return this.httpClient.del({
      resource: `${this.resource}/dto`,
      id,
      location: this.location,
    });
  }

  findOne(
    id: number | string,
    fields?: string,
    disableSpinner?: boolean
  ): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/dto`,
      id,
      fields: this.calculeFields(fields),
      location: this.location,
      disableSpinner,
    });
  }

  findById(id: number | string, fields?: string): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}`,
      location: this.location,
      id,
      fields,
    });
  }

  public existsContratoTemporario(id: number): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/contrato-temporario/${id}`,
    });
  }

  public dataPosseNomeacao(id: number): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/data-posse-nomeacao/${id}`,
    });
  }

  public existsByPostoTrabalhoId(id: number): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/exists-by-posto-trabalho/${id}`,
      disableSpinner: true,
    });
  }

  findFuncionarioComDireito(search: any): Observable<Funcionario[]> {
    return this.httpClient.get({
      resource: `${this.resource}/com-direito-ferias`,
      search: this.buildSearch(this.getSearch(search)),
      disableSpinner: true,
    });
  }

  findFuncionarioComDireitoNaoDesligado(search: any): Observable<Page<any>> {
    return this.httpClient.get({
      resource: `${this.resource}/com-direito-ferias-nao-desligado`,
      search: this.buildSearch(this.getSearch(search)),
      disableSpinner: true,
    });
  }


  findAll(
    page?: PageParameters,
    search?: string,
    fields?: string,
    disableSpinner?: boolean
  ): Observable<Page<FuncionarioDTO>> {
    return this.httpClient.get({
      resource: `${this.resource}`,
      page,
      search: this.buildSearch(search),
      disableSpinner,
      fields: this.calculeFields(fields),
    });
  }

  findAllDTOMovimentacaoPessoal(
    page?: PageParameters,
    search?: string,
    fields?: string,
    disableSpinner?: boolean
  ): Observable<Page<FuncionarioDTO>> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/movimentacao-pessoal`,
      page,
      search: this.buildSearch(search),
      disableSpinner,
      fields: this.calculeFields(fields),
    });
  }

  findAllReduzido(
    page?: PageParameters,
    search?: string,
    fields?: string,
    disableSpinner?: boolean
  ): Observable<Page<FuncionarioDTO>> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/reduzido`,
      page,
      search: this.buildSearch(search),
      fields: this.calculeFields(fields),
      disableSpinner,
    });
  }

  findAllReduzidoAutocomplete(
    page?: PageParameters,
    search?: string,
    fields?: string,
    disableSpinner?: boolean
  ): Observable<Page<FuncionarioDTO>> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/reduzido/autocomplete`,
      page,
      search: this.buildSearch(search),
      fields: this.calculeFields(fields),
      disableSpinner,
    });
  }

  findAllFuncionarioLancamentoDto(
    page?: PageParameters,
    search?: string,
    fields?: string,
    disableSpinner?: boolean
  ): Observable<Page<FuncionarioDTO>> {
    return this.httpClient.get({
      resource: `${this.resource}/funcionarioLancamentoDto`,
      page,
      search: this.buildSearch(search),
      disableSpinner,
    });
  }

  findFuncionariosConsideraDesligadosComplementar(
    page?: PageParameters,
    search?: string,
    fields?: string,
    disableSpinner?: boolean
  ): Observable<Page<FuncionarioDTO>> {
    return this.httpClient.get({
      resource: `${this.resource}/funcionarios-considera-desligados-complementar`,
      page,
      search: this.buildSearch(search),
      disableSpinner,
    });
  }

  findAutoCompleteCompetencia(
    page: PageParameters,
    competenciaId: number,
    search?: string,
    disableSpinner?: boolean
  ): Observable<Page<any>> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/autocomplete-competencia/${competenciaId}`.concat(search ? `?search=${search}` : ''),
      page,
      disableSpinner
    });
  }

  findAllEntity(
    page?: PageParameters,
    search?: string,
    fields?: string,
    disableSpinner?: boolean
  ): Observable<Page<FuncionarioDTO>> {
    return this.httpClient.get({
      resource: `${this.resource}`,
      page,
      search: this.buildSearch(search),
      disableSpinner,
      fields: this.calculeFields(fields),
    });
  }

  findFuncionarioParcelaById = (
    id: number
  ): Observable<FuncionarioParcelaDTO> => {
    return this.httpClient.get({
      resource: `/api/funcionarioparcela/${id}`,
      disableSpinner: true,
    });
  };

  existsByConvenioBancario(id: any): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/exists/by-convenio/${id}`,
      disableSpinner: true,
    });
  }

  updateConvenioBancario(
    funcionario: any,
    convenio: any
  ): Observable<ConvenioBancario> {
    return this.httpClient.put({
      resource: `${this.resource}/update/convenio/${funcionario.id}`,
      body: convenio,
      disableSpinner: true,
    });
  }

  findByProdutividade(produtividadeId: number): Observable<Funcionario> {
    return this.httpClient.get({
      resource: `${this.resource}/produtividade/${produtividadeId}`,
      disableSpinner: true,
    });
  }

  updateProdutividade(funcionariosId: FuncionarioReduzido[], produtividade: Produtividade): Observable<void> {
    return this.httpClient.put({
      resource: `${this.resource}/produtividade/${produtividade.id}`,
      body: funcionariosId,
    });
  }

  findByCondicoesAmbientaisDoTrabalho(
    id: number,
    page: PageParameters
  ): Observable<Page<CondicoesAmbientaisFuncionario>> {
    return this.httpClient.get({
      resource: `${this.resource}/by-condicoes-ambientais-de-trabalho/${id}`,
      page,
    });
  }

  findFuncionarioLancamentoDTO(
    id: number,
    competenciaId: number,
    disableSpinner?: boolean
  ): Observable<any> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/funcionario-lancamento/${id}/${competenciaId}`,
      disableSpinner
    });
  }

  findOneFuncionarioSimplificadoListarDTOByDataInicio(id: number, dataInicio: Date): Observable<FuncionarioSimplificadoDTO> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/find-one-funcionario-simplificado/${id}?dataInicio=${dataInicio}`
    });
  }

  getRespostasAgrupadas(
    funcionalidade: string,
    referenciaId?: string
  ): Observable<GrupoResposta[]> {
    return this.httpClient.get({
      resource: `/api/funcionario-campodinamico/respostas-agrupadas?funcionalidade=${funcionalidade}&referenciaId=${referenciaId}`
    });
  }

  findFuncionariosByPessoaId(pessoaId: number, isEditOrDelete: boolean): Observable<Funcionario[]> {
    return this.httpClient.get({
      resource: `${this.resource}/funcionarios-por-pessoa/${pessoaId}/${isEditOrDelete}`,
    });
  }

  funcionarioHasPrevidenciaRgps(funcionarioId: number): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/has-previdencia-rgps/${funcionarioId}`,
    });
  }

  findAutoCompleteSimplificado(
    search?: string
  ): Observable<Page<any>> {
    return this.httpClient.get({
      resource: `${this.resource}/dto/autocomplete-simplificado`.concat(search ? `?search=${search}` : ''),
      page: new PageParameters(15, 0, 'matricula,asc')
    }).pipe(
      map(page => page.content)
    );
  }

  existsByCpfAndFuncionarioId(cpf: string, funcionarioId: number): Observable<boolean> {
    if (!cpf || !funcionarioId) {
      return of(false);
    }

    return this.httpClient.get({
      resource: `${this.resource}/exists-by-cpf-and-funcionarioid?cpf=${cpf}&funcionarioId=${funcionarioId}`,
      disableSpinner: true,
    });
  }

  public findFuncionariosHoristasCompetencia(competenciaId: number): Observable<FuncionarioOption[]> {
    return this.httpClient.get({
      resource: `${this.resource}/horistas-competencia/${competenciaId}`,
    });
  }

  public findFuncionariosQuadroSalarialByCargoId(cargoId: number, search: string, somenteAtivos: Boolean, somenteDesligados: Boolean, page: PageParameters): Observable<Page<FuncionarioQuadroSalarial>> {
    return this.httpClient.get({
      resource: `${this.resource}/funcionario-plano-salarial/cargo/${cargoId}?search=${search}&somenteAtivos=${somenteAtivos}&somenteDesligados=${somenteDesligados}`,
      disableSpinner: true,
      page: page,
    });
  }

  public isFuncionarioNomeadoById(funcionarioId: number): Observable<boolean>  {
    return this.httpClient.get({
      resource: `${this.resource}/is-funcionario-nomeado/${funcionarioId}`,
      disableSpinner: false,
    })
  }

  public getMatriculasDuploVinculoByPessoaId(pessoaId: number): Observable<number[]>{
    return this.httpClient.get({
      resource: `${this.resource}/matriculas-duplo-vinculo/${pessoaId}`,
      disableSpinner: false,
    });
  }

  public possuiMatriculaPrincipal(matriculas: number[]): Observable<boolean> {
    return this.httpClient.get({
      resource: `${this.resource}/possui-matricula-principal?matriculas=${matriculas.join(',')}`,
      disableSpinner: false,
    });
  }

  public findAllTableServidor(matriculas: string, search?: string, page?: PageParameters): Observable<Page<TableServidorReduzido>> {
    return this.httpClient.post({
      resource: `${this.resource}/dto/table-servidor`,
      body: !!matriculas ? matriculas : '0',
      page: page,
      search: this.buildSearch(search),
    });
  }

}


export interface CondicoesAmbientaisFuncionario {
  id: number;
  funcionarioId: number;
  situacao: string;
  matricula: string;
  nome: string;
  cnpjCpf: string;
  codigoLotacao: string;
  cargo: string;
  classe: string;
  statusEsocial: EnumItem;
  tipoCadastro: EnumItem;
}

export interface ForaDoIntervaloDTO {
  existeForaDoIntervalo: boolean;
  funcionariosId: number[];
}

export interface FuncionarioParcelaDTO {
  id: number;
  parcela: number;
  competenciaCalculo: string;
  dataPagamento: Date;
  valorParcela: number;
}
